// src/components/admin/employees/AddEmployeeWizard.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useEmployees } from '../../../contexts/EmployeeContext';
import employeeService from '../../../services/employee.service';
import BasicDetails from './wizard/BasicDetails';
import JobDetails from './wizard/JobDetails';
import WorkDetails from './wizard/WorkDetails';
import Compensation from './wizard/Compensation';

const AddEmployeeWizard = ({ departments, onClose, onSubmit }) => {
    const { theme } = useTheme();
    const { employees } = useEmployees();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Step 1: Basic Details
        workCountry: '',
        firstName: '',
        middleName: '',
        lastName: '',
        displayName: '',
        gender: '',
        dateOfBirth: '',
        nationality: '',
        numberSeries: 'Auto',
        employeeNumber: '',
        workEmail: '',
        mobileNumber: '',
        countryCode: '+91',

        // Step 2: Job Details
        joiningDate: '',
        jobTitle: '',
        timeType: 'Full Time',
        legalEntity: '',
        department: '',
        location: '',
        workerType: 'Permanent',
        reportingManager: '',
        probationPolicy: 'Default Probation Policy',
        noticePeriod: 'Default Notice Period',

        // Step 3: Work Details
        inviteToLogin: false,
        leavePlan: '',
        holidayList: '',
        attendanceTracking: true,
        shift: '',
        weeklyOff: 'Standard Weekly Off Policy',
        attendanceNumber: '',
        attendanceCaptureScheme: 'Biometric Capture Scheme',
        attendanceTrackingPolicy: '',
        overtime: '',

        // Step 4: Compensation
        ctc: '',
        currency: 'INR',
        effectiveDate: '',
        payrollFrequency: 'Monthly',

        

        // Bank Details
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        branchName: '',

        // Tax Information
        panNumber: '',
        uanNumber: '',
    });

    // Generate next employee number
    const generateEmployeeNumberLocal = () => {
        const existingNumbers = employees
            .map(emp => emp.employee_code || emp.employeeNumber)
            .filter(code => code && code.startsWith('EMP'))
            .map(code => parseInt(code.replace('EMP', '')))
            .filter(num => !isNaN(num));

        const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
        const nextNumber = maxNumber + 1;
        return `EMP${String(nextNumber).padStart(4, '0')}`;
    };

    // Auto-generate employee number when number series is Auto
    useEffect(() => {
        if (formData.numberSeries === 'Auto') {
            // Try backend first (sees all employees including inactive), fall back to local
            employeeService.getNextEmployeeCode().then(result => {
                if (result?.code) {
                    updateFormData({ employeeNumber: result.code });
                } else {
                    updateFormData({ employeeNumber: generateEmployeeNumberLocal() });
                }
            }).catch(() => {
                updateFormData({ employeeNumber: generateEmployeeNumberLocal() });
            });
        } else if (formData.numberSeries === 'Manual') {
            updateFormData({ employeeNumber: '' });
        }
    }, [formData.numberSeries]);

    const steps = [
        { number: 1, label: 'BASIC DETAILS' },
        { number: 2, label: 'JOB DETAILS' },
        { number: 3, label: 'WORK DETAILS' },
        { number: 4, label: 'COMPENSATION' }
    ];

    // Validate each step
    const validateStep = (stepNumber) => {

        switch (stepNumber) {
            case 1: // Basic Details
                return formData.workCountry && formData.firstName && formData.lastName &&
                       formData.displayName && formData.gender && formData.dateOfBirth &&
                       formData.nationality && formData.employeeNumber && formData.workEmail &&
                       formData.mobileNumber && formData.mobileNumber.length === 10;
            case 2: // Job Details
                return formData.joiningDate && formData.jobTitle && formData.legalEntity &&
                       formData.department && formData.location && formData.workerType;
            case 3: // Work Details
                return formData.leavePlan && formData.holidayList && formData.shift_id  &&
                       (!formData.attendanceTracking || (formData.shift_id  && formData.weeklyOff && formData.attendanceCaptureScheme));
            case 4: // Compensation
                return formData.ctc && formData.currency && formData.effectiveDate && formData.payrollFrequency;
            default:
                return false;
        }
    };

    // Check if all required steps are valid (excluding compensation)
    const areRequiredStepsValid = () => {
        return validateStep(1) && validateStep(2) && validateStep(3);
    };

    // Check if ALL steps including compensation are valid
    const areAllStepsValid = () => {
        return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(4);
    };

    // Get step status color
    const getStepColor = (stepNumber) => {
        if (validateStep(stepNumber)) {
            return 'bg-green-600 text-white'; // Valid - Green
        } else if (stepNumber < currentStep) {
            return 'bg-red-600 text-white'; // Invalid and passed - Red
        } else if (stepNumber === currentStep) {
            return theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'; // Current step
        } else {
            return theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'; // Not reached yet
        }
    };

    const handleNext = () => {
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        } else {
            onSubmit(formData);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleStepClick = (stepNumber) => {
        setCurrentStep(stepNumber);
    };

    const handleSkipAndComplete = () => {
        // Skip compensation and submit with current data
        onSubmit(formData);
    };

    const updateFormData = (newData) => {
        setFormData({ ...formData, ...newData });
    };

    // Check if employee ID already exists
    const isEmployeeIdTaken = (empId) => {
        return employees.some(emp =>
            (emp.employee_code || emp.employeeNumber) === empId
        );
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <BasicDetails formData={formData} updateFormData={updateFormData} theme={theme} isEmployeeIdTaken={isEmployeeIdTaken} />;
            case 2:
                return <JobDetails formData={formData} updateFormData={updateFormData} theme={theme} departments={departments} />;
            case 3:
                return <WorkDetails formData={formData} updateFormData={updateFormData} theme={theme} />;
            case 4:
                return <Compensation formData={formData} updateFormData={updateFormData} theme={theme} />;
            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className={`h-screen flex flex-col ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                    {/* Header */}
                    <div className={`flex justify-between items-center px-6 py-4 border-b ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <h2 className={`text-xl font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Add Employee Wizard</h2>
                        <button onClick={onClose} className={`${
                            theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                        }`}>
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Stepper */}
                    <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between max-w-2xl mx-auto">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.number}>
                                    <div className="flex flex-col items-center">
                                        <button
                                            type="button"
                                            onClick={() => handleStepClick(step.number)}
                                            className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs transition-colors ${getStepColor(step.number)}`}
                                        >
                                            {step.number}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleStepClick(step.number)}
                                            className={`mt-1.5 text-xs font-medium ${
                                            validateStep(step.number)
                                                ? 'text-green-600'
                                                : currentStep === step.number
                                                    ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                    : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}
                                        >
                                            {step.label}
                                        </button>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-3 ${
                                            validateStep(step.number)
                                                ? 'bg-green-600'
                                                : theme === 'dark'
                                                    ? 'bg-gray-700'
                                                    : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    <div className="max-w-4xl mx-auto">
                        {renderStep()}
                    </div>
                </div>

                {/* Footer */}
                <div className={`px-6 py-3 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                } flex justify-between items-center`}>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`px-5 py-2 text-sm rounded-md border ${
                                theme === 'dark'
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className={`px-5 py-2 text-sm rounded-md border ${
                                currentStep === 1
                                    ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                                    : theme === 'dark'
                                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Back
                        </button>
                    </div>

                    <div className="flex gap-3">
                        {/* Skip button only on compensation step */}
                        {currentStep === 4 && (
                            <button
                                type="button"
                                onClick={handleSkipAndComplete}
                                disabled={!areRequiredStepsValid()}
                                className={`px-5 py-2 text-sm rounded-md border ${
                                    !areRequiredStepsValid()
                                        ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                                        : theme === 'dark'
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Skip & Complete
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleNext}
                            disabled={currentStep === 4 && !areAllStepsValid()}
                            className={`px-5 py-2 text-sm rounded-md ${
                                currentStep === 4 && !areAllStepsValid()
                                    ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed opacity-50'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            {currentStep === 4 ? 'Save & Complete' : 'Continue'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEmployeeWizard;
